// src/components/overview/RecentHistory.jsx
import React from 'react';
import { Link } from 'react-router-dom';
import { formatSyncType, formatDuration } from '../../utils/formatUtils';

/* ── Helpers ──────────────────────────────────────────────── */

const getTypeIcon = (type) => {
  if (!type) return 'bi-question-circle';
  if (type.includes('course')) return 'bi-book';
  if (type.includes('chapter')) return 'bi-list-nested';
  if (type.includes('content')) return 'bi-file-earmark-text';
  if (type.includes('mentor')) return 'bi-person';
  if (type.includes('attempts')) return 'bi-clipboard-check';
  if (type.includes('batch')) return 'bi-people';
  if (type.includes('users') || type === 'users-sync') return 'bi-people-fill';
  return 'bi-arrow-repeat';
};

/**
 * Returns a human-readable "time ago" string for a given date.
 */
const timeAgo = (dateStr) => {
  if (!dateStr) return 'unknown time';
  const diff = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);

  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
};

const STATUS_CONFIG = {
  completed: {
    label: 'Completed',
    icon: 'bi-check-circle-fill',
    color: 'var(--accent-green)',
    bg: 'rgba(132, 204, 22, 0.1)',
  },
  failed: {
    label: 'Failed',
    icon: 'bi-x-circle-fill',
    color: 'var(--accent-red)',
    bg: 'rgba(239, 68, 68, 0.1)',
  },
  running: {
    label: 'Running',
    icon: 'bi-arrow-repeat',
    color: 'var(--accent-blue)',
    bg: 'rgba(59, 130, 246, 0.1)',
  },
};

/* ── Component ────────────────────────────────────────────── */

const RecentHistory = ({ syncHistory }) => {
  const recent = syncHistory ? [...syncHistory].slice(0, 10) : [];

  return (
    <div className="theme-card recent-history-card">
      {/* Header */}
      <div className="recent-history-header">
        <div className="recent-history-title">
          <i className="bi bi-clock-history me-2" style={{ color: 'var(--accent-green)' }} aria-hidden="true" />
          Recent Syncs
        </div>
        <Link to="/logs" className="recent-history-view-all">
          View all <i className="bi bi-arrow-right ms-1" aria-hidden="true" />
        </Link>
      </div>

      {/* List */}
      {recent.length === 0 ? (
        <div className="recent-history-empty">
          <i className="bi bi-inbox" aria-hidden="true" />
          <span>No sync history yet</span>
        </div>
      ) : (
        <ul className="recent-history-list">
          {recent.map((sync) => {
            const cfg = STATUS_CONFIG[sync.status] || STATUS_CONFIG.running;
            return (
              <li key={sync.id} className="recent-history-item">
                {/* Type icon */}
                <div className="recent-history-item-icon" style={{ color: 'var(--text-muted)' }}>
                  <i className={`bi ${getTypeIcon(sync.type)}`} aria-hidden="true" />
                </div>

                {/* Type + time */}
                <div className="recent-history-item-info">
                  <span className="recent-history-item-type">
                    {formatSyncType(sync.type)}
                  </span>
                  <span className="recent-history-item-time">
                    {timeAgo(sync.startTime)}
                  </span>
                </div>

                {/* Duration */}
                <div className="recent-history-item-duration">
                  {formatDuration(sync)}
                </div>

                {/* Status badge */}
                <div
                  className="recent-history-item-badge"
                  style={{ color: cfg.color, backgroundColor: cfg.bg }}
                >
                  <i className={`bi ${cfg.icon} me-1`} aria-hidden="true" />
                  {cfg.label}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default RecentHistory;
